import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import { storage } from "../common/storage";

const { FiMenu, FiX, FiPhone, FiHeart } = FiIcons;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const updateCount = () => {
      const items = storage.getBookmarks();
      setBookmarkCount(items.length);
    };

    updateCount();
    window.addEventListener('storage_update', updateCount);
    window.addEventListener('storage', updateCount);
    
    return () => {
      window.removeEventListener('storage_update', updateCount);
      window.removeEventListener('storage', updateCount);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Menu", path: "/menu" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isHomeHero = location.pathname === "/" && !scrolled;
  
  const textColorClass = isOpen 
    ? "text-luxury-dark" 
    : (isHomeHero ? "text-white" : "text-luxury-dark");
  
  const logoBgClass = isOpen 
    ? "bg-white shadow-md" 
    : (scrolled ? 'bg-luxury-cream' : 'bg-white/10 backdrop-blur-sm');

  const bgClass = scrolled && !isOpen 
    ? "bg-white/95 backdrop-blur-md shadow-lg py-3" 
    : "bg-transparent py-6 md:py-8";

  const zIndexClass = isOpen ? "z-[150]" : "z-[100]";

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full transition-all duration-300 ${bgClass} ${zIndexClass}`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-8 flex justify-between items-center">
          <Link 
            to="/" 
            className={`flex items-center gap-3 text-xl md:text-2xl font-serif tracking-widest transition-colors duration-300 ${textColorClass}`}
          >
            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full p-1 flex items-center justify-center transition-all duration-300 ${logoBgClass}`}>
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757795425239-ganda%20sushi%20logo.png" 
                alt="Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="whitespace-nowrap">
              GANDA <span className="text-luxury-gold">SUSHI</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[10px] lg:text-xs font-black tracking-[0.3em] uppercase transition-all duration-300 hover:text-luxury-gold drop-shadow-sm ${
                  location.pathname === link.path 
                    ? "text-luxury-gold border-b-2 border-luxury-gold pb-1" 
                    : textColorClass
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            <Link 
              to="/bookmarks" 
              className={`relative p-2 transition-colors duration-300 hover:text-luxury-gold ${textColorClass}`}
              aria-label="View Favorites"
            >
              <SafeIcon icon={FiHeart} className="w-5 h-5" />
              {bookmarkCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-luxury-gold text-white text-[8px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                  {bookmarkCount}
                </span>
              )}
            </Link>

            <a
              href="tel:+17183801818"
              className={`luxury-button !py-3 !px-6 text-[11px] shadow-lg transition-all duration-300 ${
                isHomeHero ? "bg-luxury-gold text-white hover:bg-white hover:text-luxury-dark" : "bg-luxury-gold text-white hover:bg-luxury-dark"
              }`}
            >
              ORDER NOW
            </a>
          </div>

          {/* Mobile Toggle */}
          <div className="flex items-center gap-4 md:hidden">
            <Link 
              to="/bookmarks" 
              className={`relative p-2 transition-colors duration-300 ${textColorClass}`}
              aria-label="View Favorites"
            >
              <SafeIcon icon={FiHeart} className="w-6 h-6" />
              {bookmarkCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-luxury-gold text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {bookmarkCount}
                </span>
              )}
            </Link>
            <button
              className={`p-2 relative z-[160] transition-colors duration-300 ${
                isOpen ? "text-luxury-dark" : textColorClass
              }`}
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-label={isOpen ? "Close Menu" : "Open Menu"}
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-7 h-7" />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 200 }}
            className="fixed inset-0 bg-luxury-cream z-[110] flex flex-col p-8 pt-24 overflow-y-auto"
          >
            <div className="flex flex-col items-center justify-center gap-8 py-10 min-h-max w-full">
              {navLinks.map((link, idx) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 + 0.2 }}
                >
                  <Link
                    to={link.path}
                    className={`text-3xl font-serif tracking-wide transition-colors ${
                      location.pathname === link.path ? "text-luxury-gold" : "text-luxury-dark"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="w-full flex flex-col items-center gap-8"
              >
                <div className="w-full h-px bg-luxury-gold/20 max-w-[200px] my-4" />
                
                <a
                  href="tel:+17183801818"
                  className="luxury-button flex items-center !bg-luxury-gold text-white !px-8 !py-4 text-lg"
                >
                  <SafeIcon icon={FiPhone} className="mr-3" /> (718) 380-1818
                </a>

                <div className="mt-4 text-center text-luxury-dark/40 text-[10px] tracking-[0.2em] uppercase font-black">
                  80-08 Surrey Pl, Jamaica, NY 11432
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;